import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleDetailMap } from '@/mocks/article-detail';
import type { ArticleContentBlock } from '@/mocks/article-detail';

function ContentBlock({ block }: { block: ArticleContentBlock }) {
  switch (block.type) {
    case 'paragraph':
      return <p className="text-base md:text-lg text-foreground-700 leading-relaxed">{block.content}</p>;
    case 'heading':
      return <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-8 mb-4">{block.content}</h3>;
    case 'image':
      return block.imageUrl ? (
        <figure className="my-6">
          <img
            src={block.imageUrl}
            alt={block.imageAlt || ''}
            className="w-full rounded-xl object-cover"
            width={block.imageWidth || 800}
            height={block.imageHeight || 500}
          />
          {block.imageAlt && (
            <figcaption className="text-center text-xs text-foreground-400 mt-2">{block.imageAlt}</figcaption>
          )}
        </figure>
      ) : null;
    case 'quote':
      return (
        <blockquote className="my-6 pl-6 border-l-4 border-primary-400 bg-primary-50/50 py-4 pr-4 rounded-r-lg">
          <p className="text-base md:text-lg text-foreground-800 italic leading-relaxed">{block.content}</p>
        </blockquote>
      );
    case 'list':
      return block.items ? (
        <ul className="my-4 space-y-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-base text-foreground-700">
              <span className="mt-2 w-2 h-2 rounded-full bg-primary-400 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      ) : null;
    default:
      return null;
  }
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? articleDetailMap[id] : undefined;

  const breadcrumbSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://chigua.cn/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://chigua.cn/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://chigua.cn/article/${article.id}` },
    ],
  } : null;

  const articleSchema = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.subtitle,
    image: article.coverImage,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    dateModified: article.date,
    publisher: {
      '@type': 'Organization',
      name: '吃瓜官网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://chigua.cn/article/${article.id}`,
    },
    about: article.tags.map(tag => ({ '@type': 'Thing', name: tag })),
  } : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16 px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center py-20">
            <i className="ri-error-warning-line text-5xl text-foreground-300" />
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mt-4">文章未找到</h1>
            <p className="mt-2 text-foreground-500">该文章可能已被删除或不存在</p>
            <Link to="/" className="mt-6 inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      {breadcrumbSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      )}
      {articleSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      )}

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full min-h-[380px] md:min-h-[480px] flex flex-col items-center justify-end pb-12 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />

            <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="px-3 py-1 bg-primary-500/80 text-background-50 text-xs font-semibold rounded-full whitespace-nowrap">
                  {article.categoryName}
                </span>
                {article.tags.slice(0, 2).map(tag => (
                  <span key={tag} className="px-3 py-1 bg-white/10 text-background-50/80 text-xs rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-black text-background-50 leading-tight max-w-2xl mx-auto">
                {article.title}
              </h1>
              <p className="mt-3 text-sm md:text-base text-background-50/60 max-w-xl mx-auto">
                {article.subtitle}
              </p>
              <div className="flex items-center justify-center gap-4 mt-5 text-xs md:text-sm text-background-50/50">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line" /> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" /> {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" /> {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views.toLocaleString()} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line" /> {article.comments > 10000 ? `${(article.comments / 10000).toFixed(1)}万` : article.comments.toLocaleString()} 评论
                </span>
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line text-xs" />
                <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                  {article.categoryName}
                </Link>
                <i className="ri-arrow-right-s-line text-xs" />
                <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
              </nav>

              <article className="prose-custom">
                <div className="space-y-4">
                  {article.content.map((block, index) => (
                    <ContentBlock key={index} block={block} />
                  ))}
                </div>
              </article>

              <div className="mt-10 pt-8 border-t border-background-200">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map(tag => (
                    <Link
                      key={tag}
                      to={`/category/${article.category}`}
                      className="px-4 py-1.5 bg-background-100 text-foreground-600 text-xs rounded-full hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                <div className="flex items-center gap-6 mt-6 text-sm text-foreground-400">
                  <button className="flex items-center gap-1.5 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-thumb-up-line" /> 赞
                  </button>
                  <button className="flex items-center gap-1.5 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-share-forward-line" /> 分享
                  </button>
                  <button className="flex items-center gap-1.5 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-bookmark-line" /> 收藏
                  </button>
                </div>
              </div>
            </div>
          </section>

          {article.relatedArticles.length > 0 && (
            <section className="py-12 md:py-16 bg-background-100">
              <div className="px-4 md:px-6 max-w-5xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">相关爆料</h2>
                  <Link to={`/category/${article.category}`} className="text-sm text-primary-500 hover:underline cursor-pointer whitespace-nowrap">
                    查看更多 <i className="ri-arrow-right-line text-xs" />
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {article.relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="rounded-xl bg-background-50 overflow-hidden cursor-pointer group transition-all duration-300 hover:-translate-y-1"
                    >
                      <div className="p-3">
                        <div className="aspect-[4/3] rounded-lg overflow-hidden">
                          <img
                            src={ra.image}
                            alt={ra.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      </div>
                      <div className="px-3 pb-4">
                        <span className="text-[10px] text-primary-500 font-medium">{ra.categoryName}</span>
                        <h3 className="font-heading text-sm font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2 mt-1">
                          {ra.title}
                        </h3>
                        <div className="flex items-center justify-between mt-2 text-xs text-foreground-400">
                          <span>{ra.date}</span>
                          <span>{ra.views > 10000 ? `${(ra.views / 10000).toFixed(1)}万` : ra.views.toLocaleString()} 阅读</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}